import { useEffect, useState } from 'react';

import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API } from '@/shared/api';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { CreateSpaceRequest, InviteUserToSpaceRequest, UpdateSpaceRequest } from '@/shared/types/Space';

import { sortSpaces } from '../lib/sortSpaces';
import { TEAMSPACE_QUERY_KEYS } from '../lib/teamspaceQueryKeys';

const createSpaceAndInviteUsers = async ({ data, emails }: { data: CreateSpaceRequest; emails: string[] }) => {
  const space = await API.createSpace(data);

  if (space && emails.length > 0) {
    await API.inviteUserToSpace(space.id, { emails });
  }

  return space;
};

const fetchSpaces = async () => {
  const spaces = await API.getSpaces();

  return sortSpaces(spaces);
};

export const useGetSpaces = (enabled = true) => {
  return useQuery({
    enabled,
    queryFn: fetchSpaces,
    queryKey: TEAMSPACE_QUERY_KEYS.spaces,
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCurrentSpaceId = () => {
  const { spaceId } = useParams();

  return spaceId || '';
};

export const useInitSpaces = (isAuthenticated: boolean) => {
  const [isInitialized, setIsInitialized] = useState(false);

  const { data: spaces } = useGetSpaces(isAuthenticated);

  useEffect(() => {
    if (spaces && spaces.length > 0) {
      setIsInitialized(true);
    }
  }, [spaces]);

  return { isInitialized };
};

export const useGetSpaceById = (id: string) => {
  return useQuery({
    enabled: !!id,
    queryFn: () => API.getSpaceById(id),
    queryKey: TEAMSPACE_QUERY_KEYS.space(id),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateSpace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSpaceAndInviteUsers,
    onError: showErrorAlert,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEAMSPACE_QUERY_KEYS.spaces });
    },
  });
};

export const useUpdateSpace = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateSpaceRequest) => API.updateSpace(id, data),
    onError: showErrorAlert,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEAMSPACE_QUERY_KEYS.spaces });
      void queryClient.invalidateQueries({ queryKey: TEAMSPACE_QUERY_KEYS.space(id) });
    },
  });
};

export const useDeleteSpace = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => API.deleteSpace(id),
    onError: showErrorAlert,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEAMSPACE_QUERY_KEYS.spaces });
      void queryClient.removeQueries({
        queryKey: TEAMSPACE_QUERY_KEYS.space(id),
      });
    },
  });
};

export const useInviteUserToSpace = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InviteUserToSpaceRequest) => API.inviteUserToSpace(id, data),
    onError: showErrorAlert,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEAMSPACE_QUERY_KEYS.space(id) });
    },
  });
};

export const useDeleteUserFromSpace = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (member_id: string) => API.deleteUserFromSpace(id, member_id),
    onError: showErrorAlert,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEAMSPACE_QUERY_KEYS.space(id) });
    },
  });
};

export const useDeleteInvitedUserFromSpace = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (member_id: string) => API.deleteInvitedUserFromSpace(id, member_id),
    onError: showErrorAlert,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEAMSPACE_QUERY_KEYS.space(id) });
    },
  });
};

export const useLeaveSpace = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => API.leaveSpace(id),
    onError: showErrorAlert,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TEAMSPACE_QUERY_KEYS.spaces });
      void queryClient.removeQueries({
        queryKey: TEAMSPACE_QUERY_KEYS.space(id),
      });
    },
  });
};

type UseGetSpaceCreditsOptions = {
  // Minutes counters refresh every time they are shown instead of trusting the 5-minute cache. Counters mounting
  // together share one request: React Query dedupes a fetch that is already in flight.
  refetchOnShow?: boolean;
};

export const useGetSpaceCredits = (id: string, { refetchOnShow = false }: UseGetSpaceCreditsOptions = {}) => {
  return useQuery({
    enabled: !!id,
    queryFn: () => API.getSpaceCredits(id),
    queryKey: TEAMSPACE_QUERY_KEYS.spaceCredits(id),
    refetchOnMount: refetchOnShow ? 'always' : true,
    staleTime: 5 * 60 * 1000,
  });
};

export const useSpace = () => {
  const spaceId = useCurrentSpaceId();

  return useGetSpaceById(spaceId);
};

export const useSpaceCredits = () => {
  const spaceId = useCurrentSpaceId();

  return useGetSpaceCredits(spaceId);
};
